import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import {
  Filter,
  FilterDelegate,
  SearchType,
  TableColumnEvent,
  TableColumnFilterItem,
  TableColumnSortOption,
} from '../domain/types';
import {
  CustomFilter,
  PartialFilter,
  StrictFilter,
} from '../domain/table-filter';
import { FormControl } from '@angular/forms';
import { DropdownLabelAlignment } from '../interfaces/table-header-dropdown.interface';

@Component({
  selector: 'ps-ui-table-column-header',
  templateUrl: './table-column-header.component.html',
  styleUrls: ['./table-column-header.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableColumnHeaderComponent {
  @Input()
  public title!: string;

  @Input()
  public items: TableColumnFilterItem[] = [];

  @Input()
  public field!: string;

  @Input()
  public searchable = false;

  @Input()
  public multi: boolean = true;

  @Input()
  searchDropdownItems: boolean = false;

  @Input()
  showClearStateButton: boolean = false;

  @Input()
  public sortable = false;

  @Input()
  public filterable = false;

  @Input()
  public sorted: TableColumnSortOption = 'none';

  @Input()
  public groups: Record<number, string> = {};

  @Input()
  public set filtered(filters: Filter<Record<string, unknown>>[]) {
    this._filtered = filters;
    this._selectFilters(filters);
  }

  @Input()
  public truncateWord: boolean = true;

  @Input()
  public textAlignment: DropdownLabelAlignment = 'start';

  @Input()
  public delegate?: FilterDelegate<Record<string, unknown>>;

  @Input()
  public searchType: SearchType = 'start';

  @Output()
  public sort = new EventEmitter<TableColumnEvent>();

  @Output()
  public filter = new EventEmitter<TableColumnEvent>();

  public clearFilterButton: boolean = false;
  public filters = new FormControl([]);
  public searchText: string = '';

  public clearState() {
    const base = {
      column: this.title,
      field: this.field,
      sort: this.sorted,
      filter: [],
    };
    this._filtered = [];
    this.filter.emit(base);
    this.clearFilterButton = false;
  }
  private _filtered: Filter<Record<string, unknown>>[] = [];

  public onFilter(options: TableColumnFilterItem[]): void {
    const base = {
      column: this.title,
      field: this.field,
      sort: this.sorted,
    };

    const values = options.map((option) => option?.value);
    const filter = [
      ...this._filtered.filter((f) => !(f instanceof StrictFilter)),
      new StrictFilter(this.field, values),
    ];

    this.filter.emit({
      ...base,
      filter: this.delegate ? this._custom(values) : filter,
    });

    this.clearFilterButton = this.showClearStateButton || false;
  }

  public onSort(type: TableColumnSortOption): void {
    this.sort.emit({
      column: this.title,
      field: this.field,
      sort: this.sorted === type ? 'none' : type,
      filter: this._filtered,
    });
  }

  public onSearch(openText: string): void {
    const filterText = this.searchDropdownItems ? '' : openText;

    const data = {
      column: this.title,
      field: this.field,
      sort: this.sorted,
      searchText: openText,
      searchField: this.field,
      filter: [
        ...this._filtered.filter((f) => !(f instanceof PartialFilter)),
        new PartialFilter(this.field, filterText || '', this.searchType),
      ],
    };

    this.sort.emit(data);
  }

  public _custom(values: string[]): Filter<Record<string, unknown>>[] {
    return [
      ...this._filtered.filter((f) => !(f instanceof CustomFilter)),
      new CustomFilter(this.field, values, this.delegate!),
    ];
  }

  private _selectFilters(filters: Filter<Record<string, unknown>>[]): void {
    const filterValues = filters.map((filter) => filter.values());
    const values = filterValues
      .filter((filter) => ['option', 'custom'].includes(filter.type))
      .reduce((acc, next) => [...acc, ...next.values], [] as string[]);

    this.searchText =
      filterValues.find((filter) => filter.type === 'search')?.values[0] ?? '';

    this.filters = new FormControl(
      this.items.filter((item) => values.includes(item.value))
    );

    this.clearFilterButton = this.showClearStateButton || false;
  }
}
